import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
// The editor owns its own document-workspace shell because the route sits
// outside AppShell. TemplateEditorWorkspace composes the shared header,
// action hosts and AppFrame into responsive outline/canvas/inspector regions;
// this page retains business state, persistence and lifecycle behavior.
import { createReportPreset, fetchReportPreset } from '../lib/apiClient';
import { getTemplateLifecycle, templatesKeys } from '../lib/api/templates.gen';

// Report-preset CONTENT key (2-73, G-19). No generated factory covers
// `GET/POST /report-presets/*` — it's a legacy `lib/apiClient.ts` endpoint
// predating story 2-92's contract-first generator (see
// `lib/query/subscriptionKeys.ts` for the same situation elsewhere). A report
// preset's id IS the template id, so this nests under the generated
// `templatesKeys.detail(id)` rather than a bare `['report-preset', id]`
// literal — the same "sub-resource nested under `.detail(id)` so one
// invalidation cascades" shape `modules/reports/queries/keys.ts`'s
// `reportVersionKey` already establishes.
const reportPresetContentKey = (presetId: string) => [...templatesKeys.detail(presetId), 'content'] as const;
import { isQuotaError } from '../lib/quotaEvents';
import { errorMessage } from '../lib/errorMessage';
import { useAuth } from '../lib/auth';
import { useCapabilities } from '../lib/capabilities';
import { useTemplateAutosave } from '../hooks/useTemplateAutosave';
import {
  Badge, CardActions, Callout, Cluster, HeaderActions, SaveStateIndicator,
  SegmentedControl, Skeleton, Stack, StatusChip, Surface, ToolbarActions,
} from '../components/ui';
import type { ReportPreset, ReportSchema } from '../types';
import { EditorCanvas } from '../components/formBuilder/EditorCanvas';
import { FormPreview } from '../components/formBuilder/FormPreview';
import { ReportDesignerCanvas } from '../components/formBuilder/ReportDesigner';
import type { ReportPrintHostHandle } from '../modules/reports';
import { useReportStudio } from '../components/formBuilder/reportStudio/useReportStudio';
import { AgentPanel } from '../components/agent/AgentPanel';
import { TemplatePublishBar, STATUS_LABEL, STATUS_TONE, STATUS_WEIGHT } from '../components/templates/TemplatePublishBar';
import {
  TemplateEditorReadOnlyBoundary,
  TemplateEditorWorkspace,
} from '../components/templates/TemplateEditorWorkspace';
import type { TemplateEditorView } from '../components/templates/TemplateEditorWorkspace';
import {
  TemplateEditorOutline,
} from '../components/templates/TemplateEditorOutline';
import type { TemplateEditorSelection } from '../components/templates/TemplateEditorOutline';
import { TemplateEditorInspector } from '../components/templates/TemplateEditorInspector';
import type { TemplateEditorPatch } from '../components/templates/TemplateEditorInspector';
import { TemplateEditorDocument } from '../components/templates/TemplateEditorDocument';
import {
  TemplatePreviewInspector,
  TemplatePreviewFrame,
  TemplatePreviewOutline,
} from '../components/templates/TemplateEditorContextPanels';
import type { PreviewViewport } from '../components/templates/TemplateEditorContextPanels';
import {
  ReportStudioInspector,
  ReportStudioOutline,
  ReportStudioViewportTools,
} from '../components/templates/ReportStudioPanels';
import type {
  ReportPreviewMode,
  ReportSettingsTab,
  ReportStudioSelection,
} from '../components/templates/ReportStudioPanels';
import { registerAgentActionHandlers } from '../lib/agentActions';
import { fetchDraft } from '../lib/agentClient';
import {
  createEmptySchema, fromPresetSchema, toV1, validateFormSchema,
} from '../lib/agentShared/formSchema';
import type { FormField, FormSchemaV2, FormSection } from '../lib/agentShared/formSchema';

// Full-screen template editor — SafetyCulture-style. The presets page links
// here ("Build manually" / "Edit in builder"); the JSON never appears unless
// explicitly opened. Report customisation lands here later as a third tab.
type EditorTab = TemplateEditorView;

// Single source for the three view labels — the header's `SegmentedControl`
// and its below-`sm` drawer trigger/sheet (story 2-142) both read this one
// map, so the two chromes can never drift onto different copy (same "one
// list, two chromes" discipline `SectionNav`'s own header comment names).
const TAB_LABELS: Record<EditorTab, string> = {
  build: 'Build template',
  preview: 'Preview',
  report: 'Edit report',
};
const TAB_OPTIONS: Array<{ value: EditorTab; label: string }> = (
  ['build', 'preview', 'report'] as const
).map((value) => ({ value, label: TAB_LABELS[value] }));

/**
 * The acknowledged revision's time, as the "Saved-at" state shows it (P04 §7
 * ¶3). Time only — a draft saved four minutes ago does not need a date, and the
 * full ISO string is on the element's `title` for anyone who wants it.
 */
const ackTime = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleTimeString();
};

export function TemplateEditorPage(): JSX.Element {
  const { presetId } = useParams<{ presetId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Crash recovery is stored per ORG as well as per template — see
  // `pendingDraftKey`. Two workspaces in one browser must never be offered each
  // other's unsaved work.
  const { user } = useAuth();
  const orgId = user?.organizationId ?? null;
  // Bug fix (live user report): the Save button used to be gated ONLY on the
  // lifecycle transition table's `edit` verdict (below), which is deliberately
  // PERSON-FREE (`template.service.js`'s own `lifecycleContext`/`assertTransition`
  // doc comment — it answers questions about the ROW, never about who is
  // asking). That left every principal who can merely SEE a template
  // (`templates:read` — every staff role, including `inspector`) with a fully
  // enabled editing surface and a live Save button, even though the endpoints
  // it calls (`PATCH /report-presets/:id/draft`, `POST /report-presets/drafts`)
  // both gate on `requirePermission('templates:create')` — the exact key
  // `TemplatePublishBar`'s own controls already check via `PermissionGate`
  // (Submit/Publish/Approve/Restore/Archive). Save was the one control in this
  // family that never got that check, so an inspector saw a live Save button
  // and only learned they could not use it from the server's 403. Folded into
  // the SAME `readOnly`/`readOnlyReason` plumbing the lifecycle gate already
  // drives (the Save button's `disabled`/`disabledReason`, `handleSave`'s
  // guard, and the "This template is read-only" banner) rather than adding a
  // second gate. Pre-hydration defaults to permissive (mirrors
  // `GatedRoute`/`ModuleRoutes`'s own "capability doc not loaded yet, stay on
  // the real element" rule) so a legitimate editor never sees a denial flash.
  const { hasPermission, isHydrated: capabilitiesHydrated } = useCapabilities();
  const canEditTemplates = !capabilitiesHydrated || hasPermission('templates:create');

  // Import lifecycle. A `?import=id` deep link points at an already-extracted
  // document; the agent panel picks it up from here.
  //
  // RETIRED IN 4-2c (DOD §4 G-17): the other half of this used to be a File
  // handed over in a module-level variable (`lib/pendingImport.ts`) by the
  // library page's "Import from PDF" button, which navigated here INSTANTLY and
  // uploaded afterwards — i.e. it created the editing session before anyone had
  // seen what the sample actually mapped to. P04 §7 ¶3 requires the opposite
  // order ("Dry-run first … require confirmation before creating a draft"), so
  // that button is gone, `pendingImport.ts` is deleted with it, and the import
  // path now runs entirely inside the entry dialog. The deep link stays: it is
  // the agent plane's way of pointing the editor at an import it already has.
  // Read once from the URL and never reassigned — the only writer was the
  // retired hand-over path described above. The `uploading`/`error` phases stay
  // in the type because the agent panel still renders them for an attachment it
  // is mid-flight on.
  const [importJob] = useState<
    { name: string; phase: 'uploading' | 'ready' | 'error'; id?: string; error?: string } | null
  >(() => {
    const linkedId = searchParams.get('import');
    return linkedId ? { name: searchParams.get('name') ?? 'document.pdf', phase: 'ready', id: linkedId } : null;
  });

  const [schema, setSchema] = useState<FormSchemaV2>(() => createEmptySchema('Untitled inspection template'));
  const [tab, setTab] = useState<EditorTab>('build');
  const [dirty, setDirty] = useState(false);
  const [loadedPresetId, setLoadedPresetId] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [showJson, setShowJson] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [selection, setSelection] = useState<TemplateEditorSelection>({ kind: 'template' });
  const [previewSectionId, setPreviewSectionId] = useState<string | null>(null);
  const [previewViewport, setPreviewViewport] = useState<PreviewViewport>('web');
  const [reportSettingsTab, setReportSettingsTab] = useState<ReportSettingsTab>('style');
  const [reportPreviewMode, setReportPreviewMode] = useState<ReportPreviewMode>('web');
  // Story 4-113 — the Report tab's own selection (document/section/cover
  // page), independent of the Build tab's `selection` above: the two tabs
  // address different things (capture structure vs. presentation) and each
  // keeps its own outline/inspector context, same as `previewSectionId`
  // already does for the Preview tab.
  const [reportSelection, setReportSelection] = useState<ReportStudioSelection>({ kind: 'document' });
  // Story 4-128d — the Report tab's 'print' mode renders the paper inside an
  // ISOLATED document (an iframe over the published report artifact), so
  // `selectReportSection` below can no longer reach the section element with
  // `document.querySelector`. This handle is how the outline asks the print
  // host to scroll instead; it is null in 'web' mode, where the same-document
  // path still applies.
  const reportPrintHostRef = useRef<ReportPrintHostHandle>(null);
  // Undo/redo history — snapshots pushed on every edit, capped at 50.
  const hydratedRef = useRef<string | null>(null);
  const pastRef = useRef<FormSchemaV2[]>([]);
  const futureRef = useRef<FormSchemaV2[]>([]);
  const [historyTick, setHistoryTick] = useState(0);

  // Single-row fetch (story 4-2a): the library LIST (`listTemplates`) now
  // deliberately omits `schema`, so hydrating the canvas for an existing
  // template reads the one endpoint that still carries it
  // (`GET /report-presets/:presetId`) instead of fetching every preset and
  // finding this one by id.
  const presetQuery = useQuery({
    queryKey: reportPresetContentKey(presetId ?? ''),
    queryFn: () => fetchReportPreset(presetId as string),
    enabled: Boolean(presetId),
    staleTime: 60_000,
  });

  // P04 §6.2 (story 4-1b): "In-review/published versions are read-only." The
  // SERVER is the enforcement — a PATCH while the row is `in_review` is refused
  // — and this read is how the editor tells the truth about it early instead of
  // letting someone rearrange a form for ten minutes and discover it at save
  // time. Same query key as `TemplatePublishBar`, so react-query serves both
  // from one request and they can never disagree about the state.
  const lifecycleQuery = useQuery({
    queryKey: templatesKeys.detail(presetId ?? ''),
    queryFn: () => getTemplateLifecycle(presetId as string),
    enabled: Boolean(presetId),
    staleTime: 30_000,
  });
  // The transition table's own verdict, never a `status === 'in_review'` guess:
  // one table, and the editor reads it rather than reimplementing it.
  const editVerdict = lifecycleQuery.data?.data.actions.find((a) => a.action === 'edit');
  const readOnly = !canEditTemplates || (editVerdict ? !editVerdict.allowed : false);
  const readOnlyReason = !canEditTemplates
    ? 'You do not have permission to edit templates. Ask an owner or someone with the Template Editor role to make changes.'
    : (editVerdict?.reason ?? null);

  // Hydrate from an existing preset when editing.
  //
  // Tracked on a REF rather than on `loadedPresetId`, because resolving a
  // revision conflict by reloading has to hydrate the same id a second time
  // (story 4-2d) and `loadedPresetId` is also what tells the publish bar the row
  // exists — clearing it to force a re-hydrate would flicker the whole action
  // bar away and back.
  useEffect(() => {
    if (!presetId) return;
    const preset = presetQuery.data;
    if (!preset || hydratedRef.current === presetId) return;
    hydratedRef.current = presetId;
    setSchema(fromPresetSchema(preset.schema));
    setLoadedPresetId(presetId);
    setDirty(false);
  }, [presetId, presetQuery.data]);

  // Agent hooks: live server-owned drafts render straight into this canvas.
  useEffect(() => registerAgentActionHandlers({
    navigate: (route) => navigate(route),
    loadDraft: (id) => {
      void fetchDraft(id).then((res) => {
        if (res) {
          setDraftId(res.draft_id);
          setSchema(res.schema);
          setTab('build');
        }
      });
    },
  }), [navigate]);

  const issues = useMemo(
    () => (schema.sections.length > 0 ? validateFormSchema(schema) : []),
    [schema],
  );
  const [highlightField, setHighlightField] = useState<string | null>(null);

  // Resolve "sections[i].fields[j]…" paths to field ids so the canvas can
  // highlight the offending rows with a tooltip; anything else stays in the bar.
  const { fieldErrors, otherIssues } = useMemo(() => {
    const byField: Record<string, string[]> = {};
    const rest: string[] = [];
    for (const issue of issues) {
      const m = issue.match(/^sections\[(\d+)\]\.fields\[(\d+)\](?:\.[^:]*)?:\s*(.*)$/);
      const id = m ? schema.sections[Number(m[1])]?.fields[Number(m[2])]?.id : undefined;
      const msg = m?.[3];
      if (id && msg !== undefined) (byField[id] ??= []).push(msg);
      else rest.push(issue);
    }
    return { fieldErrors: byField, otherIssues: rest };
  }, [issues, schema]);

  // ── The acknowledgement protocol (P04 §7 ¶3, story 4-2d) ────────────────
  //
  // Everything about "is this saved" now comes from here. The hook owns the four
  // states, the crash-recovery snapshot and the revision this tab believes the
  // server holds; the page owns only the rendering and the two callbacks that
  // put the server's answer back on screen.
  const handleAcknowledged = useCallback(() => {
    // A save STAGES A DRAFT, which is a lifecycle change: it is what makes
    // `submit` (or `publish`) legal. The action bar reads that from the server's
    // transition table, so the table has to be re-read — otherwise the bar keeps
    // serving the pre-save row for `staleTime` and shows "Submit for review"
    // disabled on a template that now has something to submit.
    void queryClient.invalidateQueries({ queryKey: templatesKeys.detail(presetId ?? '') });
    void queryClient.invalidateQueries({ queryKey: templatesKeys.lists() });
  }, [queryClient, presetId]);

  const handleReload = useCallback(() => {
    // The other tab won. Take THEIR copy: drop the hydration marker so the
    // refetched preset lands on the canvas, and refetch both reads (content and
    // lifecycle) so nothing on screen is a mix of the two revisions.
    hydratedRef.current = null;
    pastRef.current = [];
    futureRef.current = [];
    void queryClient.invalidateQueries({ queryKey: reportPresetContentKey(presetId ?? '') });
    void queryClient.invalidateQueries({ queryKey: templatesKeys.detail(presetId ?? '') });
  }, [queryClient, presetId]);

  const handleRecoveredApplied = useCallback((recoveredSchema: FormSchemaV2) => {
    // The recovered snapshot is now the acknowledged truth, so it belongs on the
    // canvas. Until the retry succeeded the canvas deliberately showed the LAST
    // ACKNOWLEDGED revision — P04 §7 ¶3 says a refresh "reopens the last
    // acknowledged revision", and painting unacknowledged bytes as if they were
    // saved is the exact claim this story exists to stop.
    setSchema(recoveredSchema);
    setDirty(false);
  }, []);

  const autosave = useTemplateAutosave({
    templateId: loadedPresetId,
    orgId,
    schema,
    serverRevision: lifecycleQuery.data?.data.revision ?? null,
    // Read-only rows (in_review / archived, P04 §6.2) are refused by the server
    // anyway; autosaving into that refusal would spend the user's attention on
    // an error the transition table already explained in the banner above.
    enabled: !readOnly && Boolean(loadedPresetId),
    onAcknowledged: handleAcknowledged,
    onReload: handleReload,
    onRecoveredApplied: handleRecoveredApplied,
  });

  // "Unsaved" is the SERVER's gap, not this component's flag, for a template that
  // exists. `dirty` survives only for the one case that has no revision yet: a
  // brand-new template that has never been committed.
  const unsaved = loadedPresetId ? autosave.unacknowledged : dirty;

  // CREATE ONLY, now. Updating an existing template goes through the
  // acknowledged path (`useTemplateAutosave` → `saveTemplateDraft`); this
  // remains for the one case that has no row and therefore no revision yet — the
  // `/app/template-editor` route with no id, where a template is being built
  // before it exists. Every entry path in 4-2c already creates the row first, so
  // this is the residual manual path, not the main one.
  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = toV1(schema) as ReportSchema;
      return createReportPreset({ name: schema.title, schema: payload, tags: [], sampleReport: null });
    },
    onSuccess: async (preset: ReportPreset) => {
      // `templatesKeys.lists()` as a prefix reaches every list surface reading
      // templates — the library page's own cursor pages and (after this
      // story's consumer migrations) the option pickers on Inspections /
      // Inspection types — since react-query's default invalidation match is
      // a prefix match, not an exact one. This replaces the old
      // `['report-presets']` invalidation the single unpaged list used to key
      // on.
      await queryClient.invalidateQueries({ queryKey: templatesKeys.lists() });
      await queryClient.invalidateQueries({ queryKey: reportPresetContentKey(preset._id) });
      // A save STAGES A DRAFT, which is a lifecycle change: it is what makes
      // `submit` (or `publish`) legal. The action bar reads that from the
      // server's transition table, so the table has to be re-read — without
      // this the bar keeps serving the pre-save row for `staleTime` and shows
      // "Submit for review" disabled on a template that now has something to
      // submit. The whole point of one server-owned table is undone by a client
      // that forgets to ask it again.
      await queryClient.invalidateQueries({ queryKey: templatesKeys.detail(preset._id) });
      // Mark it hydrated so the hydration effect does not immediately replace
      // the canvas the user is standing in with a refetch of what was just sent.
      hydratedRef.current = preset._id;
      setLoadedPresetId(preset._id);
      setDirty(false);
      setSaveError(null);
    },
    onError: (error: unknown) => {
      // Template-quota 402s (story 2-18) are already routed to the QuotaTeaser
      // by the shared apiClient interceptor (2-16) — skip the inline error bar
      // so the teaser is the single surface (same pattern as the five legacy
      // PaywallModal handlers).
      if (isQuotaError(error)) return;
      // G-27: the server's message, never axios's transport summary. This line
      // used to read `error instanceof Error ? error.message : …`, which is
      // exactly the shape the gate exists to kill.
      setSaveError(errorMessage(error));
    },
  });

  const handleChange = useCallback((next: FormSchemaV2) => {
    setSchema((prev) => {
      pastRef.current = [...pastRef.current.slice(-49), prev];
      futureRef.current = [];
      return next;
    });
    setDirty(true);
    // Told explicitly rather than inferred from schema identity: hydration, an
    // agent-driven load and a conflict reload all replace the schema object
    // without a person having typed anything, and none of them is an edit to
    // autosave.
    autosave.markEdited();
    setHistoryTick((t) => t + 1);
  }, [autosave]);

  const undo = useCallback(() => {
    setSchema((prev) => {
      const last = pastRef.current[pastRef.current.length - 1];
      if (!last) return prev;
      pastRef.current = pastRef.current.slice(0, -1);
      futureRef.current = [...futureRef.current, prev];
      return last;
    });
    setDirty(true);
    autosave.markEdited();
    setHistoryTick((t) => t + 1);
  }, [autosave]);

  const redo = useCallback(() => {
    setSchema((prev) => {
      const next = futureRef.current[futureRef.current.length - 1];
      if (!next) return prev;
      futureRef.current = futureRef.current.slice(0, -1);
      pastRef.current = [...pastRef.current, prev];
      return next;
    });
    setDirty(true);
    autosave.markEdited();
    setHistoryTick((t) => t + 1);
  }, [autosave]);

  // Story 4-113 — Report Studio's OWN typed-command/undo domain (see
  // `useReportStudio.ts`'s header comment for why it is a separate stack
  // from `pastRef`/`futureRef` above, not a rewrite of it). Every dispatch/
  // undo/redo here still calls `handleChange`, so autosave/dirty-tracking
  // keep working exactly as before for the Report tab's edits — this hook
  // adds a typed, validated command layer in front of that pipeline.
  //
  // KNOWN LIMITATION, stated explicitly rather than silently: because a
  // report-layout command's effect (`schema.report`) also flows through
  // `handleChange` (needed so it round-trips through the SAME acknowledged-
  // save pipeline the Build tab uses), the Build tab's whole-schema
  // `pastRef`/`futureRef` stack above ALSO records every Report-tab edit.
  // Undo/redo BUTTONS delegate to whichever stack matches the active tab
  // (below), so normal single-tab use is unaffected, but a user who
  // alternates Undo between the Build and Report tabs in the same session
  // can desync the two stacks (each stays internally consistent; they can
  // disagree with each other about what "the next undo" is). Full
  // unification into one stack is reasonable follow-up work once more
  // Report Studio state exists to justify it — not attempted here to avoid
  // rewriting the Build tab's own, unrelated undo mechanism in a story
  // scoped to Report Studio.
  const reportStudio = useReportStudio({ schema, onSchemaChange: handleChange });

  const activeUndo = tab === 'report' ? reportStudio.undo : undo;
  const activeRedo = tab === 'report' ? reportStudio.redo : redo;

  // Cmd/Ctrl+Z and Shift+Cmd/Ctrl+Z
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return;
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return; // let inputs keep native undo
      e.preventDefault();
      if (e.shiftKey) activeRedo(); else activeUndo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeUndo, activeRedo]);
  void historyTick; // re-render trigger for button disabled states

  const handleSave = () => {
    setSaveError(null);
    setHighlightField(null);
    if (readOnly) { setSaveError(readOnlyReason ?? 'This template cannot be edited right now.'); return; }
    // An EXISTING template saves through the acknowledged path, and deliberately
    // without the local validity gate below. Autosave is already sending these
    // bytes on a timer, so a button that refused to send exactly what the timer
    // sends anyway would be theatre — and worse, it would tell a user with one
    // half-finished field that their work is unsaveable when the draft is the
    // very place half-finished work belongs. Validity is enforced where it
    // matters: submit and publish both refuse an invalid draft (P04 §6.2), and
    // the issue count stays on screen the whole time.
    if (loadedPresetId) { autosave.saveNow(); return; }
    if (!schema.title.trim()) { setSaveError('Give the template a title first'); return; }
    if (issues.length > 0) {
      const firstBad = Object.keys(fieldErrors)[0];
      if (firstBad) {
        setHighlightField(firstBad);
        setTab('build');
        setSaveError(`Fix ${issues.length} issue${issues.length > 1 ? 's' : ''} — the first one is highlighted below`);
      } else {
        setSaveError(`Fix ${issues.length} issue${issues.length > 1 ? 's' : ''} first: ${otherIssues[0]}`);
      }
      return;
    }
    if (schema.sections.length === 0) { setSaveError('Add at least one section'); return; }
    saveMutation.mutate();
  };

  const patchTemplate = useCallback((patch: { title?: string; description?: string | undefined }) => {
    const next: FormSchemaV2 = { ...schema };
    if (patch.title !== undefined) next.title = patch.title;
    if ('description' in patch) {
      if (patch.description === undefined) delete next.description;
      else next.description = patch.description;
    }
    handleChange(next);
  }, [handleChange, schema]);

  const patchSection = useCallback((sectionId: string, patch: TemplateEditorPatch<FormSection>) => {
    const next: FormSchemaV2 = JSON.parse(JSON.stringify(schema));
    const section = next.sections.find((candidate) => candidate.id === sectionId);
    if (!section) return;
    Object.assign(section, patch);
    if ('description' in patch && patch.description === undefined) delete section.description;
    handleChange(next);
  }, [handleChange, schema]);

  const patchField = useCallback((fieldId: string, patch: TemplateEditorPatch<FormField>) => {
    const next: FormSchemaV2 = JSON.parse(JSON.stringify(schema));
    const field = next.sections.flatMap((section) => section.fields).find((candidate) => candidate.id === fieldId);
    if (!field) return;

    const oldKey = field.key;
    Object.assign(field, patch);
    if ('description' in patch && patch.description === undefined) delete field.description;
    if ('min' in patch && patch.min === undefined) delete field.min;
    if ('max' in patch && patch.max === undefined) delete field.max;
    if ('step' in patch && patch.step === undefined) delete field.step;
    if (patch.key && patch.key !== oldKey) {
      for (const section of next.sections) {
        for (const candidate of section.fields) {
          for (const condition of candidate.visibleWhen ?? []) {
            if (condition.field === oldKey) condition.field = patch.key;
          }
          for (const rule of candidate.requirements ?? []) {
            if (rule.when.field === oldKey) rule.when.field = patch.key;
          }
        }
      }
      next.relations = next.relations.map((relation) => (relation.type === 'dependsOn'
        ? {
          ...relation,
          field: relation.field === oldKey ? patch.key! : relation.field,
          on: relation.on === oldKey ? patch.key! : relation.on,
        }
        : { ...relation, fields: relation.fields.map((key) => (key === oldKey ? patch.key! : key)) }));
    }
    handleChange(next);
  }, [handleChange, schema]);

  useEffect(() => {
    if (selection.kind === 'template') return;
    const section = schema.sections.find((candidate) => candidate.id === selection.sectionId);
    if (!section || (selection.kind === 'field' && !section.fields.some((field) => field.id === selection.fieldId))) {
      setSelection({ kind: 'template' });
    }
  }, [schema.sections, selection]);

  // Story 4-113 — same "a referenced section was deleted from the template"
  // edge case, for the Report tab's own selection. `useReportStudio`'s own
  // `pruneOrphanedSections` handles the analogous case for `presentation`
  // data; this is the parallel guard for the SELECTION itself, mirroring
  // the Build tab's effect immediately above.
  useEffect(() => {
    if (reportSelection.kind !== 'section') return;
    if (!schema.sections.some((section) => section.id === reportSelection.sectionId)) {
      setReportSelection({ kind: 'document' });
    }
  }, [schema.sections, reportSelection]);

  const headerChips = (loadedPresetId && lifecycleQuery.data) || draftId ? (
    <>
      {loadedPresetId && lifecycleQuery.data && (
        <span data-testid="editor-status">
          <StatusChip
            tone={STATUS_TONE[lifecycleQuery.data.data.status]}
            label={STATUS_LABEL[lifecycleQuery.data.data.status]}
            value={lifecycleQuery.data.data.status}
            {...(STATUS_WEIGHT[lifecycleQuery.data.data.status]
              ? { weight: STATUS_WEIGHT[lifecycleQuery.data.data.status] }
              : {})}
          />
        </span>
      )}
      {loadedPresetId
        && lifecycleQuery.data?.data.draftVersion !== null
        && lifecycleQuery.data?.data.draftVersion !== undefined && (
        <Badge tone="warning">Unpublished changes</Badge>
      )}
      {draftId && <Badge tone="primary" radius="pill">AI draft live</Badge>}
    </>
  ) : undefined;

  const headerTools = (
    <ToolbarActions
      size="sm"
      maxVisible={2}
      data-testid="editor-history-actions"
      actions={[
        {
          id: 'undo',
          label: 'Undo',
          icon: <ArrowUturnLeftIcon className="h-4 w-4" />,
          iconOnly: true,
          pinned: true,
          onClick: activeUndo,
          // Story 4-113 — tab-aware: the Report tab's own typed-command
          // stack (`reportStudio`) while it is active, the Build/Preview
          // tabs' whole-schema stack otherwise (see `activeUndo`'s own
          // comment above for the two-stack architecture).
          disabled: tab === 'report' ? !reportStudio.canUndo : pastRef.current.length === 0,
          'data-testid': 'editor-undo',
        },
        {
          id: 'redo',
          label: 'Redo',
          icon: <ArrowUturnRightIcon className="h-4 w-4" />,
          iconOnly: true,
          pinned: true,
          onClick: activeRedo,
          disabled: tab === 'report' ? !reportStudio.canRedo : futureRef.current.length === 0,
          'data-testid': 'editor-redo',
        },
      ]}
    />
  );

  const previewViewportTools = tab === 'preview' ? (
    <SegmentedControl
      aria-label="Preview viewport"
      value={previewViewport}
      onChange={setPreviewViewport}
      size="sm"
      options={[
        {
          value: 'web',
          label: <span className="sr-only">Web</span>,
          'aria-label': 'Web',
          icon: <ComputerDesktopIcon className="h-3.5 w-3.5" />,
        },
        {
          value: 'tablet',
          label: <span className="sr-only">Tablet</span>,
          'aria-label': 'Tablet',
          icon: <DeviceTabletIcon className="h-3.5 w-3.5" />,
        },
        {
          value: 'mobile',
          label: <span className="sr-only">Mobile</span>,
          'aria-label': 'Mobile',
          icon: <DevicePhoneMobileIcon className="h-3.5 w-3.5" />,
        },
      ]}
    />
  ) : tab === 'report' ? (
    // Story 4-113 — "viewport/page format" lives in the header, per this
    // story's own interaction contract, same slot the Preview tab's
    // web/tablet/mobile switcher above already uses.
    <ReportStudioViewportTools previewMode={reportPreviewMode} onPreviewModeChange={setReportPreviewMode} />
  ) : undefined;

  const documentMetadata = (
    <>
      {headerChips}
      <SaveStateIndicator
        kind={autosave.state.kind}
        data-testid="save-state"
        {...(autosave.state.kind === 'saved' ? { title: autosave.state.acknowledgedAt } : {})}
      >
        {autosave.state.kind === 'saving' && 'Saving…'}
        {autosave.state.kind === 'saved'
          && `Saved ${ackTime(autosave.state.acknowledgedAt)} · revision ${autosave.state.revision}`}
        {autosave.state.kind === 'failed' && 'Save failed'}
        {autosave.state.kind === 'conflict' && 'Conflict — not saved'}
      </SaveStateIndicator>
      {unsaved && (
        <span data-testid="unsaved-indicator" className="text-xs text-muted-foreground">
          Unsaved changes
        </span>
      )}
    </>
  );

  const renderLifecycle = (noticeTargetTestId?: string) => (
    <Cluster gap={2} align="center" justify="end">
      <HeaderActions
        size="sm"
        maxVisible={1}
        actions={[
          {
            id: 'save',
            label: loadedPresetId ? 'Save changes' : 'Save template',
            variant: 'secondary',
            loading: saveMutation.isPending || autosave.state.kind === 'saving',
            disabled: saveMutation.isPending || autosave.state.kind === 'saving' || readOnly,
            ...(readOnlyReason ? { disabledReason: readOnlyReason } : {}),
            onClick: handleSave,
            'data-testid': 'save-now',
          },
        ]}
      />
      {loadedPresetId && (
        <Stack gap={1} className="min-w-0">
          <TemplatePublishBar
            templateId={loadedPresetId}
            dirty={unsaved}
            {...(noticeTargetTestId ? { noticeTargetTestId } : {})}
            onChanged={() => { void queryClient.invalidateQueries({ queryKey: templatesKeys.lists() }); }}
          />
        </Stack>
      )}
    </Cluster>
  );

  const notices = (
    <Stack gap={3}>
      {autosave.recovered && (
        <Callout
          tone="warning"
          title="Unconfirmed changes were found"
          data-testid="draft-recovery"
          actions={(
            <CardActions
              align="start"
              size="sm"
              actions={[
                { id: 'retry', label: 'Retry save', variant: 'primary', onClick: autosave.retryRecovered, 'data-testid': 'recovery-retry' },
                { id: 'discard', label: 'Discard them', variant: 'ghost', onClick: autosave.discardRecovered, 'data-testid': 'recovery-discard' },
              ]}
            />
          )}
        >
          Changes from {ackTime(autosave.recovered.savedAt)} were never confirmed by the server
          {' '}(they were made against revision {autosave.recovered.ackRevision}). This template is showing the last
          {' '}saved version. Retry the unsaved changes, or discard them.
        </Callout>
      )}

      {autosave.state.kind === 'conflict' && (
        <Callout
          tone="warning"
          title="This template moved while you were editing"
          data-testid="save-conflict"
          actions={(
            <CardActions
              align="start"
              size="sm"
              actions={[
                { id: 'reload', label: 'Reload theirs', variant: 'secondary', onClick: autosave.reload, 'data-testid': 'conflict-reload' },
                { id: 'overwrite', label: 'Overwrite with mine', variant: 'primary', onClick: autosave.overwrite, 'data-testid': 'conflict-overwrite' },
              ]}
            />
          )}
        >
          {autosave.state.message}
        </Callout>
      )}

      {autosave.state.kind === 'failed' && (
        <Callout
          tone="danger"
          title="Save failed"
          data-testid="save-failed"
          actions={(
            <ToolbarActions
              size="sm"
              actions={[
                {
                  id: 'retry',
                  label: 'Retry',
                  variant: 'secondary',
                  onClick: autosave.saveNow,
                  'data-testid': 'save-retry',
                },
              ]}
            />
          )}
        >
          {autosave.state.message}
        </Callout>
      )}

      {readOnly && (
        <Callout tone="warning" title="This template is read-only">
          {readOnlyReason}
        </Callout>
      )}

      {(saveError || issues.length > 0) && (
        <Callout
          tone={saveError ? 'danger' : 'warning'}
          title={saveError ? 'This could not be saved' : 'This template has validation issues'}
          data-testid="save-error-banner"
        >
          {saveError
            ? saveError
            : `${issues.length} validation issue${issues.length > 1 ? 's' : ''}${
              Object.keys(fieldErrors).length > 0 ? ' — highlighted in the form' : `: ${otherIssues[0]}`
            }`}
        </Callout>
      )}
    </Stack>
  );

  const activePreviewSectionId = schema.sections.some((section) => section.id === previewSectionId)
    ? previewSectionId
    : (schema.sections[0]?.id ?? null);
  const selectPreviewSection = (sectionId: string) => {
    setPreviewSectionId(sectionId);
    document.getElementById(`template-preview-section-${sectionId}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Story 4-113 — "selecting a page/section/… focuses it on the canvas and
  // changes the right inspector" (the shell's own interaction contract).
  // `data-report-section-id` (`ReportRender.tsx`) is the one stable hook
  // both the outline (here) and a direct canvas click
  // (`handleCanvasSelectSection` below) resolve a selection through.
  const selectReportSection = (sectionId: string) => {
    setReportSelection({ kind: 'section', sectionId });
    // Story 4-128d — in 'print' mode the anchor is inside the isolated print
    // document, where no `querySelector` from this page can ever reach it, so
    // the host is asked to scroll it over the typed message channel. The
    // handle answers `false` whenever there is no live print host ('web' mode,
    // or a frame that has not announced itself yet), which is precisely when
    // the original same-document path below is still the right one. Both
    // branches produce the same smooth scroll-to-top of the section.
    if (reportPrintHostRef.current?.scrollToSection(sectionId)) return;
    document
      .querySelector(`[data-report-section-id="${CSS.escape(sectionId)}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  // A canvas click reports `ReportRender`'s own `section.key`, which for the
  // synthetic 'overview' row (`reportPreviewContent.ts`'s own comment) is
  // NOT a real template section id — that block IS the document-level
  // summary, so clicking it selects the document rather than a phantom
  // section the outline could never have offered in the first place.
  const handleCanvasSelectSection = (sectionKey: string) => {
    if (sectionKey === 'overview') { setReportSelection({ kind: 'document' }); return; }
    setReportSelection({ kind: 'section', sectionId: sectionKey });
  };

  const outline = tab === 'build' ? (
    <TemplateEditorOutline
      schema={schema}
      selection={selection}
      fieldErrors={fieldErrors}
      onSelect={setSelection}
    />
  ) : tab === 'preview' ? (
    <TemplatePreviewOutline
      schema={schema}
      activeSectionId={activePreviewSectionId}
      onSelectSection={selectPreviewSection}
    />
  ) : (
    <TemplateEditorReadOnlyBoundary disabled={readOnly} contained>
      <ReportStudioOutline
        schema={schema}
        selection={reportSelection}
        onSelect={(next) => (next.kind === 'section' ? selectReportSection(next.sectionId) : setReportSelection(next))}
        dispatch={reportStudio.dispatch}
      />
    </TemplateEditorReadOnlyBoundary>
  );

  const inspector = tab === 'build' ? (
    <TemplateEditorInspector
      schema={schema}
      selection={selection}
      disabled={readOnly}
      onTemplateChange={patchTemplate}
      onSectionChange={patchSection}
      onFieldChange={patchField}
    />
  ) : tab === 'preview' ? (
    <TemplatePreviewInspector schema={schema} />
  ) : (
    <TemplateEditorReadOnlyBoundary disabled={readOnly} contained>
      <ReportStudioInspector
        schema={schema}
        presentation={reportStudio.presentation}
        selection={reportSelection}
        settingsTab={reportSettingsTab}
        onSettingsTabChange={setReportSettingsTab}
        dispatch={reportStudio.dispatch}
      />
    </TemplateEditorReadOnlyBoundary>
  );

  const outlineLabel = tab === 'build'
    ? 'Template outline'
    : tab === 'preview'
      ? 'Preview sections'
      : 'Report outline';
  const inspectorLabel = tab === 'build'
    ? 'Template settings'
    : tab === 'preview'
      ? 'Preview options'
      : 'Report settings';

  return (
    <>
      <TemplateEditorWorkspace
        title={schema.title || 'Untitled inspection template'}
        metadata={documentMetadata}
        onBack={() => navigate('/app/report-presets')}
        tools={headerTools}
        contextTools={previewViewportTools}
        view={tab}
        viewOptions={TAB_OPTIONS}
        onViewChange={setTab}
        outline={outline}
        outlineLabel={outlineLabel}
        inspector={inspector}
        inspectorLabel={inspectorLabel}
        renderLifecycle={renderLifecycle}
      >
        <TemplateEditorReadOnlyBoundary disabled={readOnly}>
          {presetId && presetQuery.isLoading && !loadedPresetId ? (
            <Skeleton className="h-64" />
          ) : tab === 'build' ? (
            <TemplateEditorDocument
              title={schema.title}
              description={schema.description}
              notices={notices}
            >
              <Stack gap={6}>
                {schema.sections.length === 0 && (
                  <Callout tone="info" title="Start your template">
                    Add a section from the canvas, or open the Agent and describe the inspection.
                  </Callout>
                )}
                <EditorCanvas
                  schema={schema}
                  onChange={handleChange}
                  fieldErrors={fieldErrors}
                  highlightField={highlightField}
                  focusedSectionId={selection.kind === 'template' ? null : selection.sectionId}
                  focusedFieldId={selection.kind === 'field' ? selection.fieldId : null}
                  floatingAddRail={false}
                  onFocusChange={(sectionId, fieldId) => setSelection(
                    fieldId
                      ? { kind: 'field', sectionId, fieldId }
                      : { kind: 'section', sectionId },
                  )}
                />
                <Stack gap={2} align="start">
                  <ToolbarActions
                    size="sm"
                    maxVisible={1}
                    actions={[
                      {
                        id: 'json',
                        label: `${showJson ? 'Hide' : 'View'} JSON`,
                        variant: 'ghost',
                        onClick: () => setShowJson((value) => !value),
                        pressed: showJson,
                        'data-testid': 'editor-json-toggle',
                      },
                    ]}
                  />
                  {showJson && (
                    <Surface as="section" variant="flat" padding="md" className="max-h-80 w-full overflow-auto">
                      <pre className="whitespace-pre-wrap break-words font-mono text-xs text-muted-foreground">
                        {JSON.stringify(toV1(schema), null, 2)}
                      </pre>
                    </Surface>
                  )}
                </Stack>
              </Stack>
            </TemplateEditorDocument>
          ) : tab === 'preview' ? (
            <TemplatePreviewFrame viewport={previewViewport}>
              {notices}
              <FormPreview schema={schema} focusedSectionId={activePreviewSectionId} />
            </TemplatePreviewFrame>
          ) : (
            <Stack gap={3}>
              {notices}
              <ReportDesignerCanvas
                schema={schema}
                previewMode={reportPreviewMode}
                selectedSectionId={reportSelection.kind === 'section' ? reportSelection.sectionId : null}
                onSelectSection={handleCanvasSelectSection}
                printHostRef={reportPrintHostRef}
              />
            </Stack>
          )}
        </TemplateEditorReadOnlyBoundary>
      </TemplateEditorWorkspace>

      <AgentPanel
        variant="drawer"
        agentName="assistant"
        context={{ route: 'template-editor', draftId: draftId ?? undefined }}
        busy={importJob?.phase === 'uploading'}
        busyLabel="Uploading and reading the document — scanned PDFs take a little longer…"
        autoPrompt={importJob?.phase === 'ready' && importJob.id
          ? `Build a template from the imported sample document (import_id: ${importJob.id}, filename: ${importJob.name}). First read the WHOLE document with import_get_text (page through offsets until has_more is false). Then create a draft and rebuild the CONTENT faithfully first: pages/sections/tables with the original wording, matching response sets, logic — plus obviously useful evidence fields the document lacks (photos, signature), noting they are additions. Show it live while you work and validate. THEN offer styling choices via ui_suggest_actions: apply the original document's style, apply one of my saved report styles (report_style_list), or keep the DefectSnap default — and offer to save the final style with report_style_save. Ask me before committing.`
          : undefined}
        autoPromptLabel={importJob ? `Build a template from "${importJob.name}"` : undefined}
        attachment={importJob
          ? {
            name: importJob.name,
            detail: importJob.phase === 'uploading'
              ? 'Uploading & transcribing…'
              : importJob.phase === 'error'
                ? `Import failed — ${importJob.error ?? 'try again'}`
                : 'Imported sample document',
          }
          : undefined}
        onArtifact={(id, next) => {
          setDraftId(id);
          setSchema(next);
          setDirty(true);
        }}
      />
    </>
  );
}
